import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ActionPlanSummaryWidget from "../../components/ActionPlan/ActionPlanSummaryWidget";
import ValeursPage from "../../components/ValeursPage/ValeursPage";
import {
    getSelectedService,
    PoleScoreDetailsWidget,
    ServiceScoreDetailsWidget,
    ServicesScoreTableWidget,
    ValueFilterWidget
} from "../../components/Overview/OverviewWidgets";
import PoleServicesMapWidget from "../../components/Overview/PoleServicesMapWidget";
import { DecisionAlert } from "../../models/DecisionSupport";
import {
    OverviewScope,
    PoleDashboardViewModel,
    ValueCode
} from "../../models/OverviewDashboard";
import OverviewDashboardService from "../../services/OverviewDashboardService";
import { getCurrentCalendarMonthPeriod } from "../../utils/StatisticsPeriod";
import styles from "./VueEnsemblePage.module.css";

const POLE_IDENTIFIER = "CHU-NANCY";
const MEASURE_ID = "Meas-UpCareValeurometreByService";

const VueEnsemblePage: FunctionComponent = () => {
    const navigate = useNavigate();
    const [dashboard, setDashboard] = useState<PoleDashboardViewModel>();
    const [scope, setScope] = useState<OverviewScope>("pole");
    const [selectedValue, setSelectedValue] = useState<ValueCode>("bienveillance");
    const [selectedServiceId, setSelectedServiceId] = useState<string>();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>();

    const fhirBaseUrl = process.env.REACT_APP_FHIR_URL ?? "fhir";
    const statisticsPeriod = useMemo(() => getCurrentCalendarMonthPeriod(), []);

    const dashboardService = useMemo(() => new OverviewDashboardService({
        fhirBaseUrl,
        measureId: MEASURE_ID,
        periodStart: statisticsPeriod.periodStart,
        periodEnd: statisticsPeriod.periodEnd
    }), [fhirBaseUrl, statisticsPeriod]);

    useEffect(() => {
        let mounted = true;

        dashboardService.load(POLE_IDENTIFIER)
            .then((result) => {
                if (!mounted) return;
                setDashboard(result);
                setSelectedServiceId(undefined);
                setScope("pole");
            })
            .catch((reason) => {
                console.error(reason);
                if (mounted) setError("Impossible de charger la vue d'ensemble.");
            })
            .finally(() => {
                if (mounted) setLoading(false);
            });

        return () => {
            mounted = false;
        };
    }, [dashboardService]);

    const selectedService = dashboard
        ? getSelectedService(dashboard, selectedServiceId)
        : undefined;

    const actionPlanOrganizationReferences = scope === "services" && selectedService
        ? [selectedService.service.reference]
        : dashboard?.services.map((item) => item.service.reference) ?? [];

    const handleScopeChange = (nextScope: OverviewScope) => {
        setScope(nextScope);

        if (nextScope === "pole") {
            setSelectedServiceId(undefined);
            return;
        }

        if (!selectedServiceId && dashboard?.services[0]) {
            setSelectedServiceId(dashboard.services[0].service.id);
        }
    };

    const handleServiceSelect = (serviceId: string) => {
        setSelectedServiceId(serviceId);
        setScope("services");
    };

    const handleAlertAction = (alert: DecisionAlert) => {
        const params = new URLSearchParams({
            value: alert.suggestion.value,
            scope: alert.scope,
            organization: alert.organizationReference
        });
        navigate(`/accompagnement?${params.toString()}`);
    };

    return (
        <ValeursPage titleKey="title.overview" loading={false} needsLogin={true}>
            <div className={styles.page}>
                {loading && <p className={styles.state}>Chargement du pôle et de ses services...</p>}
                {error && <p className={styles.error}>{error}</p>}

                {dashboard && !loading && !error && (
                    <div className={styles.layout}>
                        <ValueFilterWidget
                            selectedValue={selectedValue}
                            onValueChange={setSelectedValue}
                        />

                        <main className={styles.mainColumn}>
                            <PoleServicesMapWidget
                                fhirBaseUrl={fhirBaseUrl}
                                pole={dashboard.pole}
                                services={dashboard.services}
                                scope={scope}
                                selectedServiceId={selectedServiceId}
                                selectedValue={selectedValue}
                                onScopeChange={handleScopeChange}
                                onServiceSelect={handleServiceSelect}
                            />
                            <ServicesScoreTableWidget
                                dashboard={dashboard}
                                selectedServiceId={selectedServiceId}
                                selectedValue={selectedValue}
                                onServiceSelect={handleServiceSelect}
                                fhirBaseUrl={fhirBaseUrl}
                                measureId={MEASURE_ID}
                                scope={scope}
                                onAlertAction={handleAlertAction}
                            />
                        </main>

                        <aside className={styles.rightColumn}>
                            {scope === "pole" ? (
                                <PoleScoreDetailsWidget
                                    dashboard={dashboard}
                                    selectedValue={selectedValue}
                                />
                            ) : (
                                <ServiceScoreDetailsWidget
                                    service={selectedService}
                                    selectedValue={selectedValue}
                                />
                            )}
                            <ActionPlanSummaryWidget
                                fhirBaseUrl={fhirBaseUrl}
                                value={selectedValue}
                                organizationReferences={actionPlanOrganizationReferences}
                                poleOrganization={{
                                    reference: dashboard.pole.reference,
                                    name: dashboard.pole.name
                                }}
                            />
                        </aside>
                    </div>
                )}
            </div>
        </ValeursPage>
    );
};

export default VueEnsemblePage;
